import { FormEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createGodown, getDistrict, listGodowns } from "../../lib/api";
import { currentClaims } from "../../lib/auth";
import { InviteForm } from "../../components/admin/InviteForm";
import { EnterpriseBanner } from "../../components/admin/EnterpriseBanner";
import { GrainLimitsForm } from "../../components/admin/GrainLimitsForm";
import { ReportsTable } from "../../components/admin/ReportsTable";
import {
  Card,
  Crumbs,
  ErrorNote,
  Loading,
  PageTitle,
} from "../../components/admin/ui";

/** District detail: its godowns, an add-godown form, and per-godown
 * "invite QC agent". Reachable by super, enterprise_admin, state_head,
 * and the district_head for this district.
 *
 * listGodowns() returns every godown the caller can see (backend
 * scope-filters), so we filter client-side to this district. */
export function AdminDistrict() {
  const { id } = useParams();
  const districtId = Number(id);
  const qc = useQueryClient();

  const distQ = useQuery({
    queryKey: ["district", districtId],
    queryFn: () => getDistrict(districtId),
  });
  const godownsQ = useQuery({
    queryKey: ["godowns"],
    queryFn: listGodowns,
  });

  const district = distQ.data?.district;
  const godowns = (godownsQ.data?.godowns ?? []).filter(
    (g) => g.district_id === districtId,
  );
  const godownIds = godowns.map((g) => g.id);

  // Role-aware parent breadcrumbs: only include a crumb the caller's
  // role can actually open, so a district_head doesn't click "State"
  // and get bounced to "/". The EnterpriseBanner still gives them the
  // enterprise context.
  const role = currentClaims()?.role;
  const isSuper = role === "agrilens_super";
  const canOpenState =
    isSuper || role === "enterprise_admin" || role === "state_head";

  return (
    <div className="space-y-6">
      <Crumbs
        items={[
          ...(isSuper
            ? [{ label: "Enterprises", to: "/admin/enterprises" }]
            : []),
          ...(canOpenState && district
            ? [{ label: "State", to: `/admin/state/${district.state_id}` }]
            : []),
          { label: district?.name ?? `District #${districtId}` },
        ]}
      />
      <PageTitle
        eyebrow="District"
        title={district?.name ?? `District #${districtId}`}
        subtitle="Godowns in this district. Add godowns, set grain parameters, and invite QC agents."
      />

      <EnterpriseBanner />

      {district && (
        <Link
          to={`/district/${districtId}`}
          className="inline-flex items-center gap-1 text-sm text-agrilens-green hover:underline"
        >
          View rollup dashboard for this district →
        </Link>
      )}

      {distQ.error && <ErrorNote error={distQ.error} />}

      <AddGodownForm
        districtId={districtId}
        onCreated={() => qc.invalidateQueries({ queryKey: ["godowns"] })}
      />

      {godownsQ.isLoading && <Loading />}
      {godownsQ.error && <ErrorNote error={godownsQ.error} />}
      {godownsQ.data && (
        <div className="space-y-3">
          {godowns.length === 0 && (
            <div className="text-sm text-gray-500">
              No godowns yet — add one above.
            </div>
          )}
          {godowns.map((g) => (
            <div key={g.id} className="bg-white rounded shadow p-4 space-y-4">
              <div>
                <div className="text-lg font-semibold text-agrilens-green-dark">
                  {g.name}
                </div>
                {g.place && (
                  <div className="text-xs text-gray-500">{g.place}</div>
                )}
              </div>
              {/* Per-grain FCI limits — the district head configures how
                  each grain grades at this godown, mirroring the app. */}
              <GrainLimitsForm godown={g} />
              <InviteForm
                role="godown_qc_agent"
                roleLabel="QC agent"
                scopeType="godown"
                scopeId={g.id}
              />
            </div>
          ))}
        </div>
      )}

      {/* All QC reports across the godowns in this district — the
          district head has the same auditing reach as an admin in the
          app. Scope-filtered on the backend; narrowed to this
          district's godowns here. */}
      <Card title="Reports from QC agents in this district">
        <ReportsTable godownIds={godownIds} />
      </Card>
    </div>
  );
}

function AddGodownForm({
  districtId,
  onCreated,
}: {
  districtId: number;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      createGodown({
        district_id: districtId,
        name: name.trim(),
        place: place.trim() || undefined,
      }),
    onSuccess: () => {
      setName("");
      setPlace("");
      onCreated();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || mut.isPending) return;
    mut.mutate();
  };

  return (
    <Card title="Add godown">
      <form onSubmit={onSubmit} className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-gray-500 mb-1">
            Godown name
          </label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="A-N-1"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-gray-500 mb-1">
            Place (optional)
          </label>
          <input
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            placeholder="Saharsa town"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={mut.isPending}
          className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {mut.isPending ? "Adding…" : "Add godown"}
        </button>
      </form>
      {mut.isError && (
        <div className="mt-2">
          <ErrorNote error={mut.error} />
        </div>
      )}
    </Card>
  );
}
